import { jwtDecode } from 'jwt-decode';
import { identityClient } from '@/services/api.client';
import { 
  AuthResponse, 
  BackendAuthResponse,
  BackendRefreshResponse,
  ChangePasswordRequest,
  AdminResetPasswordRequest,
  MessageResponse,
  DecodedUserData, 
  JWTPayload, 
  LoginCredentials,
  RevokeTokenRequest
} from '@/shared/types/auth.types';
import { Role } from '@/shared/constants/roles';
import { Permission } from '@/shared/constants/permissions';
import {
  STORAGE_KEYS,
  validateTokenStructure,
  isTokenExpiringSoon as checkTokenExpiringSoon,
  getBrowserFingerprint,
} from '@/lib/security';
import { logger } from '@/lib/logger';
import { TOKEN_EXPIRY_THRESHOLD_SECONDS } from '@/shared/constants/app.constants';

// Storage event channel for cross-tab synchronization
const LOGOUT_CHANNEL = 'jade_logout_channel';

class AuthService {
  private broadcastChannel: BroadcastChannel | null = null;
  private fingerprint: string | null = null;

  constructor() {
    // Initialize broadcast channel for cross-tab communication
    if (typeof BroadcastChannel !== 'undefined') {
      this.broadcastChannel = new BroadcastChannel(LOGOUT_CHANNEL);
      this.setupBroadcastListener();
    }

    // Setup storage event listener for cross-tab sync
    this.setupStorageListener();

    // Initialize browser fingerprint
    this.initializeFingerprint();
  }

  /**
   * Initialize browser fingerprint for device binding
   */
  private async initializeFingerprint(): Promise<void> {
    try {
      this.fingerprint = await getBrowserFingerprint();
      localStorage.setItem(STORAGE_KEYS.FINGERPRINT, this.fingerprint);
    } catch (error) {
      logger.error('Error generating fingerprint:', error);
    }
  }

  /**
   * Setup broadcast channel listener for logout events
   */
  private setupBroadcastListener(): void {
    if (this.broadcastChannel) {
      this.broadcastChannel.onmessage = (event) => {
        if (event.data === 'logout') {
          this.handleRemoteLogout();
        } else if (event.data?.type === 'token_refresh') {
          // Sync token refresh across tabs
          this.handleRemoteTokenRefresh(event.data.accessToken, event.data.refreshToken);
        }
      };
    }
  }

  /**
   * Setup storage event listener for cross-browser-tab synchronization
   */
  private setupStorageListener(): void {
    window.addEventListener('storage', (event) => {
      // If access token was removed in another tab, logout this tab
      if (event.key === STORAGE_KEYS.ACCESS_TOKEN && event.newValue === null) {
        this.handleRemoteLogout();
      }
    });
  }

  /**
   * Handle logout event from another tab
   */
  private handleRemoteLogout(): void {
    // Clear local tokens without broadcasting (to avoid infinite loop)
    localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.REMEMBER_ME);
    sessionStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    sessionStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    
    // Reload page to reset state
    window.location.reload();
  }

  /**
   * Handle token refresh from another tab
   */
  private handleRemoteTokenRefresh(accessToken: string, refreshToken: string): void {
    const rememberMe = localStorage.getItem(STORAGE_KEYS.REMEMBER_ME) === 'true';
    this.setToken(accessToken, rememberMe);
    this.setRefreshToken(refreshToken, rememberMe);
  }

  /**
   * Login user with email and password
   * @param email - User email
   * @param password - User password
   * @param rememberMe - If true, store in localStorage, otherwise sessionStorage
   * @returns Promise with auth response
   */
  async login(
    email: string, 
    password: string, 
    rememberMe: boolean = false
  ): Promise<AuthResponse> {
    try {
      const credentials: LoginCredentials = { 
        email, 
        password,
        app: 'jade-academy',
        rememberMe 
      };
      
      // Include browser fingerprint for device binding
      const fingerprintHeader = this.fingerprint || await getBrowserFingerprint();
      
      // Backend returns capitalized property names
      const backendResponse = await identityClient.post<BackendAuthResponse, LoginCredentials>(
        '/Auth/login',
        credentials,
        {
          headers: {
            'X-Device-Fingerprint': fingerprintHeader,
          }
        }
      );
      
      // Map backend response to camelCase
      const data: AuthResponse = {
        token: backendResponse.token,
        refreshToken: backendResponse.refreshToken,
        user: backendResponse.user
      };
      
      // Store tokens based on rememberMe preference
      this.setToken(data.token, rememberMe);
      this.setRefreshToken(data.refreshToken, rememberMe);
      
      // Store rememberMe flag
      localStorage.setItem(STORAGE_KEYS.REMEMBER_ME, rememberMe.toString());
      
      return data;
    } catch (error) {
      logger.error('Login error:', error);
      throw error;
    }
  }

  /**
   * Logout user and clear all tokens
   * Clears local storage immediately and attempts to revoke on backend in background
   */
  async logout(): Promise<void> {
    // Get refresh token before clearing (for background revoke)
    const refreshToken = this.getRefreshToken();
    const accessToken = this.getToken();
    
    // 1. Clear all auth-related storage IMMEDIATELY
    localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.REMEMBER_ME);
    sessionStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    sessionStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    
    // 2. Broadcast logout to other tabs
    if (this.broadcastChannel) {
      this.broadcastChannel.postMessage('logout');
    }
    
    // 3. Try to revoke on backend (fire and forget - don't block logout)
    if (refreshToken && accessToken) {
      void this.revokeTokenOnBackend(refreshToken, accessToken).catch(() => {
        // Silently ignore errors - user is already logged out locally
      });
    }
  }

  /**
   * Attempt to revoke refresh token on backend
   * This is a fire-and-forget operation that should not block logout
   * @private
   */
  private async revokeTokenOnBackend(refreshToken: string, accessToken: string): Promise<void> {
    try {
      const { identityClient: tempClient } = await import('@/services/api.client');
      
      await tempClient.post<{ Message: string }, RevokeTokenRequest>(
        '/Auth/revoke',
        { refreshToken },
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          }
        }
      );
    } catch {
      // Ignore all errors - this is best effort only
      // User is already logged out locally
    }
  }

  /**
   * Get access token from storage
   * Checks both sessionStorage and localStorage based on rememberMe preference
   * 
   * @returns Token string or null
   */
  getToken(): string | null {
    // Check sessionStorage first (for non-rememberMe sessions)
    const sessionToken = sessionStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    if (sessionToken && validateTokenStructure(sessionToken)) {
      return sessionToken;
    }

    // Then check localStorage (for rememberMe sessions)
    const localToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    if (localToken && validateTokenStructure(localToken)) {
      return localToken;
    }

    return null;
  }

  /**
   * Store access token in appropriate storage
   * 
   * @param token - JWT access token
   * @param rememberMe - If true, use localStorage; otherwise sessionStorage
   */
  setToken(token: string, rememberMe: boolean = false): void {
    if (!validateTokenStructure(token)) {
      logger.error('Invalid token structure');
      return;
    }

    if (rememberMe) {
      // Store in localStorage for persistent sessions
      localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, token);
      sessionStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    } else {
      // Store in sessionStorage for temporary sessions
      sessionStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, token);
      localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    }
  }

  /**
   * Get refresh token from storage
   * @returns Refresh token string or null
   */
  getRefreshToken(): string | null {
    return localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || 
           sessionStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
  }

  /**
   * Store refresh token in appropriate storage
   * @param refreshToken - Refresh token
   * @param rememberMe - If true, use localStorage (7 days), otherwise sessionStorage (1 day)
   */
  setRefreshToken(refreshToken: string, rememberMe: boolean = false): void {
    if (rememberMe) {
      // Long-lived refresh token in localStorage (7 days)
      localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
      sessionStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    } else {
      // Short-lived refresh token in sessionStorage (1 day)
      sessionStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
      localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    }
  }

  /**
   * Refresh access token using refresh token
   * @returns New access token or null if refresh failed
   */
  async refreshAccessToken(): Promise<string | null> {
    try {
      const refreshToken = this.getRefreshToken();
      
      if (!refreshToken) {
        logger.warn('No refresh token available');
        return null;
      }

      const rememberMe = localStorage.getItem(STORAGE_KEYS.REMEMBER_ME) === 'true';
      const fingerprintHeader = this.fingerprint || await getBrowserFingerprint();

      // Call refresh endpoint - backend returns capitalized property names
      const backendResponse = await identityClient.post<BackendRefreshResponse>(
        '/Auth/refresh',
        { refreshToken },
        {
          headers: {
            'X-Device-Fingerprint': fingerprintHeader,
          }
        }
      );

      // Map backend response to camelCase
      const newAccessToken = backendResponse.Token;
      const newRefreshToken = backendResponse.RefreshToken;

      // Store new tokens
      this.setToken(newAccessToken, rememberMe);
      this.setRefreshToken(newRefreshToken, rememberMe);

      // Broadcast token refresh to other tabs
      if (this.broadcastChannel) {
        this.broadcastChannel.postMessage({
          type: 'token_refresh',
          accessToken: newAccessToken,
          refreshToken: newRefreshToken,
        });
      }

      return newAccessToken;
    } catch (error) {
      logger.error('Token refresh failed:', error);
      // If refresh fails, logout user
      await this.logout();
      return null;
    }
  }

  /**
   * Check if access token is expiring soon (within threshold)
   * @returns True if token expires soon
   */
  isTokenExpiringSoon(): boolean {
    const userData = this.decodeToken();
    if (!userData || !userData.exp) return true;
    
    return checkTokenExpiringSoon(userData.exp, TOKEN_EXPIRY_THRESHOLD_SECONDS);
  }

  /**
   * Decode JWT token and extract user data
   * @returns Decoded user data or null if invalid
   */
  decodeToken(): DecodedUserData | null {
    const token = this.getToken();
    
    if (!token) {
      return null;
    }

    try {
      const payload = jwtDecode<JWTPayload>(token);
      
      // Check if token is expired
      const currentTime = Math.floor(Date.now() / 1000);
      if (payload.exp && payload.exp < currentTime) {
        // Fire and forget - logout in background
        void this.logout();
        return null;
      }

      // Normalize roles to always be an array
      const roles = Array.isArray(payload.role) ? payload.role : [payload.role];

      return {
        id: payload.sub,
        email: payload['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress'],
        userName: payload['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name'],
        roles,
        permissions: payload.permission || [],
        brandId: payload.brand_id,
        departmentId: payload.department_id,
        positionId: payload.position_id,
        exp: payload.exp,
      };
    } catch (error) {
      logger.error('Error decoding token:', error);
      // Fire and forget - logout in background
      void this.logout();
      return null;
    }
  }

  /**
   * Check if user is authenticated (has valid token)
   * @returns True if authenticated, false otherwise
   */
  isAuthenticated(): boolean {
    return this.decodeToken() !== null;
  }

  /**
   * Check if user has a specific role
   * @param role - Role to check
   * @returns True if user has the role
   */
  hasRole(role: Role): boolean {
    const userData = this.decodeToken();
    if (!userData) return false;
    
    return userData.roles.includes(role);
  }

  /**
   * Check if user has any of the specified roles
   * @param roles - Array of roles to check
   * @returns True if user has at least one of the roles
   */
  hasAnyRole(roles: Role[]): boolean {
    const userData = this.decodeToken();
    if (!userData) return false;
    
    return roles.some(role => userData.roles.includes(role));
  }

  /**
   * Check if user has all of the specified roles
   * @param roles - Array of roles to check
   * @returns True if user has all of the roles
   */
  hasAllRoles(roles: Role[]): boolean {
    const userData = this.decodeToken();
    if (!userData) return false;
    
    return roles.every(role => userData.roles.includes(role));
  }

  /**
   * Check if user has a specific permission
   * @param permission - Permission to check
   * @returns True if user has the permission
   */
  hasPermission(permission: Permission): boolean {
    const userData = this.decodeToken();
    if (!userData) return false;
    
    return userData.permissions.includes(permission);
  }

  /**
   * Check if user has any of the specified permissions
   * @param permissions - Array of permissions to check
   * @returns True if user has at least one of the permissions
   */
  hasAnyPermission(permissions: Permission[]): boolean {
    const userData = this.decodeToken();
    if (!userData) return false;
    
    return permissions.some(permission => userData.permissions.includes(permission));
  }

  /**
   * Check if user has all of the specified permissions
   * @param permissions - Array of permissions to check
   * @returns True if user has all of the permissions
   */
  hasAllPermissions(permissions: Permission[]): boolean {
    const userData = this.decodeToken();
    if (!userData) return false;
    
    return permissions.every(permission => userData.permissions.includes(permission));
  }

  /**
   * Change the authenticated user's own password.
   * Requires the current password for validation.
   * After success, all refresh tokens are revoked (forces re-login on other devices).
   * @param currentPassword - The user's current password
   * @param newPassword - The new password to set
   */
  async changePassword(currentPassword: string, newPassword: string): Promise<MessageResponse> {
    const accessToken = this.getToken();
    if (!accessToken) {
      throw new Error('No hay sesión activa.');
    }

    const payload: ChangePasswordRequest = { currentPassword, newPassword };

    const response = await identityClient.post<MessageResponse, ChangePasswordRequest>(
      '/Auth/change-password',
      payload,
      {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        }
      }
    );

    return response;
  }

  /**
   * Admin: Reset another user's password.
   * Only admins in jade-academy can use this endpoint.
   * Revokes all target user's refresh tokens after reset.
   * @param userId - The identity user ID of the target user
   * @param newPassword - The new password to set
   */
  async adminResetPassword(userId: string, newPassword: string): Promise<MessageResponse> {
    const accessToken = this.getToken();
    if (!accessToken) {
      throw new Error('No hay sesión activa.');
    }

    const payload: AdminResetPasswordRequest = { newPassword };

    const response = await identityClient.post<MessageResponse, AdminResetPasswordRequest>(
      `/Auth/users/${userId}/reset-password`,
      payload,
      {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        }
      }
    );

    return response;
  }

  /**
   * Get current user data from token
   * @returns Decoded user data or null
   */
  getCurrentUser(): DecodedUserData | null {
    return this.decodeToken();
  }
}

// Export singleton instance
export const authService = new AuthService();
export default authService;

